import { Vector3 } from "three";
import type { SugarModel } from "../../../loader/SugarModel";
import {
  flattenPathAdaptive,
  pathNormalAt,
} from "../../../shape/segment/axis/pathHelpers";
import type {
  SegmentEntry,
  SegmentSpec,
  XzPoint,
} from "../../../shape/segment/types";
import type {
  MeasurementAnchor,
  ProductMeasurementAnchor,
} from "./MeasurementTypes";
import { segmentPathAnchorAtT } from "../../../core/scene/SurfaceAnchor";

export type MeasurementSnap = {
  source: ProductMeasurementAnchor;
  target: MeasurementAnchor;
  start: XzPoint;
  end: XzPoint;
  distance: number;
};

export type MeasurementPreview = Pick<
  MeasurementSnap,
  "start" | "end" | "distance"
>;

type Segment2 = { a: XzPoint; b: XzPoint; ta?: number; tb?: number };

export function productFootprint(model: SugarModel): XzPoint[] {
  model.updateMatrixWorld(true);
  const points: XzPoint[] = [];
  for (const obb of model.getCollisionWorldObbs()) {
    for (const sx of [-1, 1] as const) {
      for (const sz of [-1, 1] as const) {
        const offset = new Vector3(
          obb.halfSize.x * sx,
          0,
          obb.halfSize.z * sz,
        ).applyMatrix3(obb.rotation);
        points.push({
          x: obb.center.x + offset.x,
          z: obb.center.z + offset.z,
        });
      }
    }
  }
  if (points.length >= 3) return convexHull(points);

  const obb = model.obb;
  const c = obb.center;
  const h = obb.halfSize;
  return convexHull([
    { x: c.x - h.x, z: c.z - h.z },
    { x: c.x + h.x, z: c.z - h.z },
    { x: c.x + h.x, z: c.z + h.z },
    { x: c.x - h.x, z: c.z + h.z },
  ]);
}

export function resolveProductProductSnap(
  source: SugarModel,
  target: SugarModel,
  pointer: XzPoint,
): MeasurementSnap | null {
  if (source === target) return null;
  const first = closestPointOnPolygon(productFootprint(source), pointer);
  const second = closestPointOnPolygon(productFootprint(target), pointer);
  return {
    source: productAnchor(source, first),
    target: productAnchor(target, second),
    start: first,
    end: second,
    distance: distance(first, second),
  };
}

export function resolveProductWallSnap(
  source: SugarModel,
  entry: SegmentEntry,
  pointer: XzPoint,
): MeasurementSnap | null {
  if (entry.spec.placement) return null;
  let best:
    | { point: XzPoint; pointerDistance: number; t: number; side: 1 | 3 }
    | undefined;

  for (const side of [1, 3] as const) {
    for (const wallEdge of wallSideSegments(entry.spec, side)) {
      const projected = closestPointOnSegment(pointer, wallEdge);
      if (!best || projected.distance < best.pointerDistance) {
        const t0 = wallEdge.ta ?? 0;
        const t1 = wallEdge.tb ?? 1;
        best = {
          point: projected.point,
          pointerDistance: projected.distance,
          t: t0 + (t1 - t0) * projected.t,
          side,
        };
      }
    }
  }
  if (!best) return null;
  const start = closestPointOnPolygon(productFootprint(source), pointer);
  return {
    source: productAnchor(source, start),
    target: segmentPathAnchorAtT(
      entry.spec,
      best.t,
      best.side === 1 ? 1 : -1,
      best.side === 1
        ? entry.spec.upThickness
        : entry.spec.downThickness,
    ),
    start,
    end: best.point,
    distance: distance(start, best.point),
  };
}

export function previewProductToPointer(
  source: SugarModel,
  pointer: XzPoint,
): MeasurementPreview {
  const start = closestPointOnPolygon(productFootprint(source), pointer);
  return {
    start,
    end: pointer,
    distance: distance(start, pointer),
  };
}

function productAnchor(
  model: SugarModel,
  world: XzPoint,
): ProductMeasurementAnchor {
  const local = model.worldToLocal(new Vector3(world.x, 0, world.z));
  return {
    kind: "product",
    modelUuid: model.uuid,
    local: { x: local.x, z: local.z },
  };
}

export function wallSideSegments(spec: SegmentSpec, side: 1 | 3): Segment2[] {
  const offset = side === 1 ? spec.upThickness : -spec.downThickness;
  const samples = flattenPathAdaptive(spec.path);
  const points = samples.map(({ point, t }) => {
    const normal = pathNormalAt(spec.path, t);
    return {
      point: {
        x: point.x + normal.x * offset,
        z: point.z + normal.z * offset,
      },
      t,
    };
  });
  return points.slice(1).map((current, index) => ({
    a: points[index].point,
    b: current.point,
    ta: points[index].t,
    tb: current.t,
  }));
}

export function wallOutlineSegments(spec: SegmentSpec): Segment2[] {
  return [...wallSideSegments(spec, 1), ...wallSideSegments(spec, 3)];
}

function polygonSegments(points: XzPoint[]): Segment2[] {
  return points.map((point, index) => ({
    a: point,
    b: points[(index + 1) % points.length],
  }));
}

function convexHull(points: readonly XzPoint[]): XzPoint[] {
  const sorted = [...points].sort((a, b) => a.x - b.x || a.z - b.z);
  if (sorted.length <= 2) return sorted;
  const cross = (a: XzPoint, b: XzPoint, c: XzPoint) =>
    (b.x - a.x) * (c.z - a.z) - (b.z - a.z) * (c.x - a.x);
  const lower: XzPoint[] = [];
  for (const point of sorted) {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2]!, lower[lower.length - 1]!, point) <= 0
    ) {
      lower.pop();
    }
    lower.push(point);
  }
  const upper: XzPoint[] = [];
  for (let index = sorted.length - 1; index >= 0; index--) {
    const point = sorted[index]!;
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2]!, upper[upper.length - 1]!, point) <= 0
    ) {
      upper.pop();
    }
    upper.push(point);
  }
  lower.pop();
  upper.pop();
  return [...lower, ...upper];
}

export function closestPointOnPolygon(
  polygon: XzPoint[],
  point: XzPoint,
): XzPoint {
  let best: { point: XzPoint; distance: number } | undefined;
  for (const edge of polygonSegments(polygon)) {
    const candidate = closestPointOnSegment(point, edge);
    if (!best || candidate.distance < best.distance) {
      best = candidate;
    }
  }
  return best?.point ?? point;
}

function closestPointOnSegment(
  point: XzPoint,
  segment: Segment2,
): { point: XzPoint; distance: number; t: number } {
  const dx = segment.b.x - segment.a.x;
  const dz = segment.b.z - segment.a.z;
  const lengthSq = dx * dx + dz * dz;
  const t =
    lengthSq > 1e-12
      ? Math.max(
          0,
          Math.min(
            1,
            ((point.x - segment.a.x) * dx +
              (point.z - segment.a.z) * dz) /
              lengthSq,
          ),
        )
      : 0;
  const projected = {
    x: segment.a.x + dx * t,
    z: segment.a.z + dz * t,
  };
  return { point: projected, distance: distance(point, projected), t };
}

function distance(first: XzPoint, second: XzPoint): number {
  return Math.hypot(first.x - second.x, first.z - second.z);
}

export function closestBetweenPolygons(
  first: XzPoint[],
  second: XzPoint[],
): { first: XzPoint; second: XzPoint; distance: number } {
  let best:
    | { first: XzPoint; second: XzPoint; distance: number }
    | undefined;
  for (const a of polygonSegments(first)) {
    for (const b of polygonSegments(second)) {
      const result = closestSegmentPair(a, b);
      if (!best || result.distance < best.distance) best = result;
    }
  }
  return best ?? { first: first[0], second: second[0], distance: Infinity };
}

function closestSegmentPair(
  first: Segment2,
  second: Segment2,
): {
  first: XzPoint;
  second: XzPoint;
  distance: number;
  firstT: number;
  secondT: number;
} {
  const ux = first.b.x - first.a.x;
  const uz = first.b.z - first.a.z;
  const vx = second.b.x - second.a.x;
  const vz = second.b.z - second.a.z;
  const wx = first.a.x - second.a.x;
  const wz = first.a.z - second.a.z;
  const a = ux * ux + uz * uz;
  const b = ux * vx + uz * vz;
  const c = vx * vx + vz * vz;
  const d = ux * wx + uz * wz;
  const e = vx * wx + vz * wz;
  const denominator = a * c - b * b;
  let s = denominator > 1e-12 ? (b * e - c * d) / denominator : 0;
  let t = denominator > 1e-12 ? (a * e - b * d) / denominator : 0;
  s = Math.max(0, Math.min(1, s));
  t = c > 1e-12 ? Math.max(0, Math.min(1, (b * s + e) / c)) : 0;
  s = a > 1e-12 ? Math.max(0, Math.min(1, (b * t - d) / a)) : 0;
  const p = { x: first.a.x + ux * s, z: first.a.z + uz * s };
  const q = { x: second.a.x + vx * t, z: second.a.z + vz * t };
  return {
    first: p,
    second: q,
    distance: Math.hypot(p.x - q.x, p.z - q.z),
    firstT: s,
    secondT: t,
  };
}
